import { TokenOutlined } from "@mui/icons-material";
import { Box, Divider, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { UploadIcon } from "../../../assets/images/icons/UploadIcon";
import WarningAlertIcon from "../../../assets/images/icons/WarningAlertIcon";
import BriaInput from "../../../components/common/BriaInput/BriaInput";
import FileUploader from "../../../components/common/FileUploader/FileUploader";
import ImageUploader from "../../../components/common/ImageUploader/ImageUploader";
import StoppableButton from "../../../components/common/StoppableButton/StoppableButton";
import { BackendError } from "../../../config/axios";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { usePostHog } from "../../../hooks/usePostHog";
import { useAppStore } from "../../../hooks/useStores";
import FiboNegativePrompt from "../../Fibo/Config/NegativePrompt/FiboNegativePrompt";
import FiboNumberOfImages from "../../Fibo/Config/NumberOfImages/FiboNumberOfImages";
import FiboSeed from "../../Fibo/Config/Seed/FiboSeed";
import FiboStructuredPrompt from "../../Fibo/StructuredPrompt/FiboStructuredPrompt";
import { useScrollToSection } from "../../Fibo/hooks/useScrollToSection";
import FiboEditSteps from "../Steps/FiboEditSteps";
import { FiboEditConfigType } from "../fibo-edit-store";
import { useFiboEditImageUpload } from "../hooks/useFiboEditImageUpload";
import FiboEditError from "./Error/FiboEditError";
import styles from "./FiboEditConfig.module.scss";

const DragAndDrop = observer(() => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.config.dragAndDrop" });
	const { fiboStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const { jumpToSection } = useScrollToSection();
	const { handleUpload } = useFiboEditImageUpload(jumpToSection);

	const DescriptionComponent = ({ title, description }: { title?: string; description?: string }) => {
		return (
			<Box className={styles.imageUploaderDescriptionContainer}>
				<Typography className={styles.imageUploaderTitle}>{title}</Typography>
				<Typography className={styles.imageUploaderDescription}>{description}</Typography>
			</Box>
		);
	};

	return (
		<Box className={styles.dragAndDropWrapper}>
			<ImageUploader
				src={fiboEditStore.config.image}
				className={styles.imageUploader}
				emptyStateClassName={styles.imageUploaderEmptyState}
				inputProps={{ accept: "image/*" }}
				dropOverlayProps={{
					icon: <UploadIcon />,
					text: t("dropOverlayText"),
				}}
				description={<DescriptionComponent title={t("title")} description={t("description")} />}
				icon={<UploadIcon size={10} />}
				showIcon
				smallIcon
				hideSupportElement
				hideDelete
				customOverlayClassName={styles.customOverlay}
				onUpload={handleUpload}
			/>
		</Box>
	);
});

interface IFiboPromptProps {
	error?: BackendError;
}

const FiboPrompt = observer(({ error }: IFiboPromptProps) => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.config.prompt" });
	const { fiboStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const { jumpToSection } = useScrollToSection();
	const { handleUpload } = useFiboEditImageUpload(jumpToSection);
	const isGenerateDisabled = !fiboEditStore.config.image || !fiboEditStore.config.prompt?.length;

	return (
		<Box className={styles.inputCard}>
			<Box className={styles.promptInputContainer}>
				<BriaInput
					className={styles.textField}
					multiline
					rows={6}
					placeholder={t("placeholder")}
					onChange={(e) => (fiboEditStore.config.prompt = e.target.value)}
					InputProps={{ className: styles.inputBase }}
					value={fiboEditStore.config.prompt}
					onEnterPress={() => (!isGenerateDisabled && fiboEditStore.edit()) || undefined}
				/>
				<>
					<Divider className={styles.divider} orientation="vertical" />
					<FileUploader
						buttonText="Upload Image"
						buttonType="textMedium"
						inputProps={{ accept: "image/*" }}
						buttonProps={{ startIcon: <UploadIcon /> }}
						onUpload={handleUpload}
						buttonClassName={styles.uploadButton}
					/>
				</>
			</Box>
			{error && (
				<Box className={styles.errorContainer}>
					<FiboEditError
						error={error}
						icon={<WarningAlertIcon />}
						showDismissButton={false}
						onClear={() => (fiboEditStore.errors = {})}
					/>
				</Box>
			)}
			<Box className={styles.controlsRow}>
				<FiboEditSteps />
				<FiboSeed
					onChange={(newSeed) => (fiboEditStore.config.seed = newSeed)}
					value={fiboEditStore.config.seed}
				/>
				<FiboNegativePrompt
					onChange={(newValue) => (fiboEditStore.config.negative_prompt = newValue)}
					value={fiboEditStore.config.negative_prompt}
				/>
				<FiboNumberOfImages
					value={fiboEditStore.config.num_results}
					onChange={(newValue: number) => (fiboEditStore.config.num_results = newValue)}
				/>
			</Box>

			{/* <BriaButton className={styles.updateButton} buttonType="textMedium">
								{t("structuredPrompt.updateButton")}
							</BriaButton> */}
			{/* </Box> */}
		</Box>
	);
});

const FiboEditConfig = () => {
	const { t } = useTranslation("fibo_edit", { keyPrefix: "lab.config" });
	const { fiboStore, playgroundStore, authStore, anonymousUsageStore } = useAppStore();
	const { fiboEditStore } = fiboStore;
	const { trackEvent } = usePostHog();
	const isGenerateDisabled = !fiboEditStore.config.image || !fiboEditStore.config.prompt?.length;
	const previousResultIdRef = useRef<string | undefined>(undefined);
	const hasImage = !!fiboEditStore.config.image;
	const isAnonymous = !authStore.isLoggedIn;
	const editUsage = anonymousUsageStore.getUsage("labs_edit");

	useEffect(() => {
		const currentResultId = playgroundStore.selectedResult?.id;
		const resultChanged =
			previousResultIdRef.current !== undefined && previousResultIdRef.current !== currentResultId;

		if (resultChanged && playgroundStore.selectedResult) {
			const selectedConfig = playgroundStore.selectedResult.config as FiboEditConfigType;

			// Update the fiboEditStore config with the selected result's config
			fiboEditStore.config = {
				...fiboEditStore.config,
				image: selectedConfig.image || fiboEditStore.config.image,
				mask: selectedConfig.mask,
				negative_prompt: selectedConfig.negative_prompt || "",
				steps_num: selectedConfig.steps_num || fiboEditStore.config.steps_num,
			};

			// If switching to a result without a mask, clear the canvas and reset the mask editor
			if (!selectedConfig.mask) {
				fiboEditStore.maskEditorStore.clearCanvas();
				fiboEditStore.maskEditorStore.resetBrushConfigs();
			}
		}

		previousResultIdRef.current = currentResultId;
	}, [playgroundStore.selectedResult?.id, fiboEditStore, playgroundStore.selectedResult]);

	return (
		<>
			<Box className={styles.promptsContainer}>
				<Box className={styles.upperZone}>
					<Box className={`${styles.componentWrapper} ${!hasImage ? styles.visible : styles.hidden}`}>
						<DragAndDrop />
					</Box>
					<Box className={`${styles.componentWrapper} ${hasImage ? styles.visible : styles.hidden}`}>
						<FiboPrompt error={fiboEditStore.errors?.generate} />
					</Box>
				</Box>
				<FiboStructuredPrompt
					title={t("structuredPrompt.title")}
					infoTooltip={t("structuredPrompt.infoTooltip")}
					jsonSourceViewTooltip={t("structuredPrompt.jsonSourceView")}
					structuredViewTooltip={t("structuredPrompt.structuredView")}
					copyToClipboardTooltip={t("structuredPrompt.copyToClipboard")}
					loadingPlaceholderText={t("structuredPrompt.loadingPlaceholderText")}
					wordCountText={t("structuredPrompt.wordCount")}
					emptyStateTitle={t("structuredPrompt.emptyState.title")}
					emptyStateDescription={t("structuredPrompt.emptyState.description")}
				/>
			</Box>

			<StoppableButton
				className={styles.editButton}
				disabled={isGenerateDisabled}
				condition={fiboEditStore.isGeneratingImages || fiboEditStore.isGeneratingStructuredPrompt}
				onStopButtonClick={() => {
					fiboEditStore.stopEditImageGeneration();
					trackEvent(POSTHOG_EVENTS.FIBO_EDIT_STOP_GENERATION_CLICKED, {
						lab_variant: "FIBO-EDIT",
					});
				}}
				onButtonClick={() => {
					if (!isGenerateDisabled) {
						trackEvent(POSTHOG_EVENTS.FIBO_EDIT_GENERATE_BUTTON_CLICKED, {
							lab_variant: "FIBO-EDIT",
							prompt: fiboEditStore.config.prompt,
							num_results: fiboEditStore.config.num_results,
							has_mask: !!fiboEditStore.config.mask,
						});
						fiboEditStore.edit();
					}
				}}
				buttonLabel={
					<>
						{t("editButton")}
						{isAnonymous && (
							<span
								style={{
									marginLeft: 8,
									display: "inline-flex",
									alignItems: "center",
									gap: 4,
									fontSize: "0.85em",
									opacity: 0.9,
								}}
							>
								{editUsage.used}/{editUsage.limit}
								<TokenOutlined style={{ fontSize: "1.1em" }} />
							</span>
						)}
					</>
				}
				stopButtonLabel={t("stopButton")}
				disabledTooltip={t("editButtonDisabledTooltip")}
			/>
		</>
	);
};

export default observer(FiboEditConfig);
